import React, { useEffect, useState } from 'react';

import { FieldValues, Control, Path, FieldError, useController } from 'react-hook-form';
import { getErrorMessage } from '../Input/ValidationErrors';

// MUI
import { TextField, Checkbox, Chip } from '@mui/material';
import Autocomplete, { AutocompleteProps, AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// SharedComponents
import InputLabelText from '/packages/shared-components/forms/InputLabelText/InputLabelText';
import { getChipStyles } from '/packages/shared-components/StatusChip/StatusChip';

interface ValidateObject {
  [key: string]: (value: string | string[] | null) => boolean | string;
}

type Option = { value: string; label?: string };
type TRenderTagLabel<T extends string | Option> = T extends Option ? keyof T : never;

export interface FilterOptionsState<T> {
  inputValue: string;
  getOptionLabel: (option: T) => string;
}

export interface AutocompleteInputBaseProps<T extends FieldValues, K extends string | Option> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  hideLabel?: boolean;
  hideOptional?: boolean;
  validate?: ValidateObject;
  multiple?: boolean;
  freeSolo?: boolean;
  placeholder?: string;
  options: K[];
  renderTagLabel?: TRenderTagLabel<K>;
  selectedOnTop?: boolean;
  helperText?: string;
  changeRequested?: boolean;
  transformOption?: boolean;
  onChange?: (param: K | NonNullable<string | K> | (string | K)[] | null) => void;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: K,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode;
  autocompleteProps?: Partial<AutocompleteProps<K, boolean, boolean, boolean>>;
  startTypingText?: string;
  /** When freeSolo + transformOption, keep typed input in sync with the stored string value. */
  freeSoloSyncInput?: boolean;
  getOptionLabel?: (option: K | string) => string;
  matchStoredValue?: (option: K, storedValue: string) => boolean;
  normalizeFieldValue?: (value: unknown) => string;
  getInputDisplayValue?: (storedValue: string, options: K[]) => string;
  filterOptionsWithContext?: (options: K[], state: FilterOptionsState<K>, context: { showAllOptions: boolean }) => K[];
  showAllOptionsOnOpen?: boolean;
  hideErrorMsg?: boolean;
}

const AutocompleteInputBase = <T extends FieldValues, K extends string | Option>({
  control,
  label,
  name,
  required = false,
  hideLabel = false,
  hideOptional = false,
  multiple = false,
  freeSolo = false,
  selectedOnTop = false,
  changeRequested = false,
  placeholder = undefined,
  transformOption = false,
  helperText,
  options = [],
  validate,
  onChange,
  renderOption,
  renderTagLabel,
  autocompleteProps,
  startTypingText = 'Start typing to see results.',
  freeSoloSyncInput = false,
  getOptionLabel,
  matchStoredValue,
  normalizeFieldValue,
  getInputDisplayValue,
  filterOptionsWithContext,
  showAllOptionsOnOpen = false,
  hideErrorMsg = false,
}: AutocompleteInputBaseProps<T, K>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const {
    field: { onChange: onChangeField, value },
    fieldState,
  } = useController({ control, name, rules: { required, validate } });

  const optionToVal = (opt: K) => (typeof opt === 'string' ? opt : opt.value);
  const valToOption = (val: string, options: K[]) =>
    options.find((el) => (typeof el === 'string' ? el === val : el.value === val));

  const storedValue = freeSoloSyncInput && normalizeFieldValue ? normalizeFieldValue(value) : value;
  const resolveInputDisplayValue = (nextStoredValue: string) =>
    getInputDisplayValue ? getInputDisplayValue(nextStoredValue, options) : nextStoredValue;

  const [showAllOptions, setShowAllOptions] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    freeSoloSyncInput ? resolveInputDisplayValue(typeof storedValue === 'string' ? storedValue : '') : '',
  );

  useEffect(() => {
    if (!freeSoloSyncInput) return;

    const nextStoredValue = typeof storedValue === 'string' ? storedValue : '';
    setInputValue(resolveInputDisplayValue(nextStoredValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedValue, options, freeSoloSyncInput]);

  useEffect(() => {
    if (!freeSoloSyncInput || !normalizeFieldValue) return;

    if (value != null && typeof value === 'object') {
      onChangeField(normalizeFieldValue(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = fieldState.error as FieldError;
  const errorMessage = getErrorMessage(error);

  const handleChange = (_: unknown, select: any) => {
    if (freeSoloSyncInput) {
      if (typeof select === 'string') {
        onChangeField(select);
        setInputValue(select);
      } else if (select) {
        const nextValue = optionToVal(select as K);
        onChangeField(nextValue);
        setInputValue(resolveInputDisplayValue(nextValue));
      } else {
        onChangeField('');
        setInputValue('');
      }

      onChange && onChange(select);
      return;
    }

    onChangeField(transformOption && select ? optionToVal(select) : select);
    onChange && onChange(select);
  };

  const handleInputChange = (_: unknown, nextInputValue: string, reason: string) => {
    if (!freeSoloSyncInput) return;

    setInputValue(nextInputValue);
    if (reason === 'input' || reason === 'clear') {
      if (showAllOptionsOnOpen) {
        setShowAllOptions(false);
      }
      onChangeField(nextInputValue);
    }
  };

  const getDefault = (obj: K, prop: TRenderTagLabel<K> | 'label' | 'value' = 'value'): string => {
    if (typeof obj === 'string') return obj;
    if (Object.hasOwn(obj, prop)) return `${(obj as Option)[prop]}`;
    return (obj as Option)['value'];
  };

  const {
    onOpen: autocompleteOnOpen,
    onClose: autocompleteOnClose,
    slotProps: autocompleteSlotProps,
    ...restAutocompleteProps
  } = autocompleteProps ?? {};

  const handleOnOpen = (event: React.SyntheticEvent) => {
    if (showAllOptionsOnOpen && freeSoloSyncInput) {
      setShowAllOptions(true);
    }

    if (selectedOnTop && multiple)
      options.sort((a, b) => {
        const aSelected = value.includes(getDefault(a, 'label'));
        const bSelected = value.includes(getDefault(b, 'label'));
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });

    autocompleteOnOpen?.(event);
  };

  const handleOnClose = (event: React.SyntheticEvent, reason: string) => {
    if (showAllOptionsOnOpen && freeSoloSyncInput) {
      setShowAllOptions(false);
    }

    if (selectedOnTop && multiple)
      options.sort((a, b) => {
        return getDefault(a, 'label') < getDefault(b, 'label') ? -1 : 1;
      });

    autocompleteOnClose?.(event, reason as never);
  };

  const autocompleteValue = value !== undefined ? value : multiple ? [] : null;
  const transformedValue = freeSoloSyncInput
    ? typeof storedValue === 'string' && storedValue && matchStoredValue
      ? options.find((option) => matchStoredValue(option, storedValue)) ?? null
      : null
    : transformOption && typeof autocompleteValue === 'string'
    ? autocompleteValue
      ? valToOption(autocompleteValue, options) || null
      : null
    : autocompleteValue;

  return (
    <InputLabelText
      label={label}
      name={name}
      errorMessage={errorMessage}
      required={hideOptional ? undefined : required}
      hideLabel={hideLabel}
      helperText={helperText}
      changeRequested={changeRequested}
      hideErrorMsg={hideErrorMsg}
    >
      <Autocomplete<K, boolean, boolean, boolean>
        id={`${name}-input`}
        value={transformedValue}
        onChange={handleChange}
        onOpen={handleOnOpen}
        onClose={handleOnClose}
        {...(freeSoloSyncInput ? { inputValue, onInputChange: handleInputChange } : {})}
        multiple={multiple}
        freeSolo={freeSolo}
        disableCloseOnSelect={multiple}
        options={options}
        {...(freeSoloSyncInput && getOptionLabel ? { getOptionLabel } : {})}
        {...(freeSoloSyncInput && filterOptionsWithContext
          ? {
              filterOptions: (filterableOptions: K[], state) =>
                filterOptionsWithContext(filterableOptions, state, { showAllOptions }),
            }
          : {})}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            name={name}
            variant="outlined"
            placeholder={!value || !value.length ? placeholder : ''}
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!errorMessage}
            sx={{
              background: (theme) => theme.palette.background.paper,
              '& input::placeholder': {
                color: 'text.secondary',
                opacity: 1,
              },
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => getDefault(option) === getDefault(value)}
        renderOption={({ key, ...props }, option, state) => {
          const liKey = `${name}-option-${`${key}`.replaceAll(' ', '_')}`;
          if (multiple) {
            return (
              <li key={liKey} {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8, padding: '-15px' }}
                  checked={state.selected}
                />
                {renderOption ? renderOption(props, option, state) : getDefault(option, 'label')}
              </li>
            );
          }
          return (
            <li key={liKey} {...props}>
              {renderOption ? renderOption(props, option, state) : getDefault(option)}
            </li>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((el, index) => (
            <Chip
              sx={(theme) => ({
                ...getChipStyles('primary', 'subtle', theme.palette),
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                },
              })}
              label={getDefault(el, renderTagLabel)}
              {...getTagProps({ index })}
              key={`${name}-tag-${index}`}
            />
          ))
        }
        noOptionsText={startTypingText}
        slotProps={{
          ...autocompleteSlotProps,
          popper: {
            sx: {
              '.MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: 'transparent',
              },
              '.MuiAutocomplete-noOptions': {
                color: 'primary.main',
              },
            },
            ...autocompleteSlotProps?.popper,
          },
        }}
        {...(restAutocompleteProps as Partial<AutocompleteProps<K, boolean, boolean, boolean>>)}
      />
    </InputLabelText>
  );
};

export default AutocompleteInputBase;
